<template>
    <div v-if="profile" class="ProfileHighlights">
        <div v-if="contactPhone || contactEmail" class="mb-4">
            <a v-if="contactPhone" class="link" :href="`tel:${contactPhone}`" target="_blank">{{ contactPhone }}</a>
            <span v-if="contactPhone && contactEmail">;</span>
            <a v-if="contactEmail" class="link" :href="`mailto:${contactEmail}`" target="_blank">{{ contactEmail }}</a>
        </div>
        <IconTextRow v-if='currentJob' variant="default" icon="work">
            {{ currentJob }}
        </IconTextRow>

        <IconTextRow v-if="lastUniversity" variant="default" icon="school">
            {{ lastUniversity }}
        </IconTextRow>

        <IconTextRow v-if="customLocation" variant="default" icon="location">
            {{ customLocation }}
        </IconTextRow>

        <IconTextRow v-else-if="location" variant="default" icon="location">
            {{ location }}
        </IconTextRow>

        <slot class="hidden sm:block" name="description" />
        <slot class="hidden sm:block" name="interests" />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import orderBy from 'lodash-es/orderBy'
import type { UserProfile, School } from '#shared/types/User'
import IconTextRow from '@/components/common/IconTextRow.vue'

const props = defineProps({
    profile: {
        type: Object as () => UserProfile,
        required: true
    },
    isAnonymous: {
        type: Boolean,
        default: false
    },
    contactPhone: {
        type: String,
        default: '',
        required: false
    },
    contactEmail: {
        type: String,
        default: '',
        required: false
    },
    customLocation: {
        type: String,
        default: ''
    }
})

const location = computed(() => {
    // Long format like in Vue 2 - includes region with "kraj" suffix
    if (!props.profile) return null
    const parts = [
        props.profile.geoCity,
        props.profile.geoRegion ? `${props.profile.geoRegion} kraj` : undefined,
        props.profile.geoCountry
    ].filter(Boolean)
    return parts.join(', ') || null
})

const currentJob = computed(() => {
    const currentJob = orderBy(props.profile?.jobs ?? [], 'endYear', 'desc')[0]
    return props.isAnonymous
        ? currentJob?.position ?? ''
        : [currentJob?.position, currentJob?.org].filter(Boolean).join(' @ ')
})

const lastUniversity = computed(() => {
    // TODO: pick the last attended (or finished?)
    const university: School = (props.profile.universities || [])[0]
    if (!university) {
        return ''
    }
    return [university.school, university.address, university.specialistation, university.endYear]
        .filter(Boolean)
        .join(', ')
})
</script>